import { io, Socket } from 'socket.io-client';
import { ChatServerToClientEvents, ChatClientToServerEvents } from '@my-game/shared-types';
import { StorageHelpers } from './storage';

class ChatSocket {
  private socket: Socket<ChatServerToClientEvents, ChatClientToServerEvents> | null = null;
  private serverUrl: string = '';
  private listeners: Map<string, Set<Function>> = new Map();

  connect(serverUrl: string): void {
    // Проверяем, не подключены ли уже
    if (this.socket?.connected) {
      console.log('Chat socket already connected');
      return;
    }

    // Проверяем, не в процессе ли подключения
    if (this.socket && !this.socket.connected && this.socket.active) {
      console.log('Chat socket is already connecting');
      return;
    }

    // Если есть старый сокет, отключаем его
    if (this.socket) {
      this.disconnect();
    }

    this.serverUrl = serverUrl;
    
    console.log('Connecting to chat namespace...');
    
    // Для cookie-based авторизации не нужно передавать токен
    // Cookie будут автоматически отправлены с запросом
    this.socket = io(`${serverUrl}/chat`, {
      withCredentials: true, // Важно для отправки cookies
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      transports: ['websocket'] // Используем только websocket для стабильности
    });

    this.socket.on('connect', () => {
      console.log('Connected to chat namespace');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from chat namespace:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Chat connection error:', error.message);
    });
  }

  disconnect(): void {
    if (this.socket) {
      console.log('Disconnecting chat socket...');
      // Удаляем все обработчики
      this.listeners.forEach((callbacks, event) => {
        callbacks.forEach(callback => {
          this.socket?.off(event as any, callback as any);
        });
      });
      this.listeners.clear();
      
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  emit<K extends keyof ChatClientToServerEvents>(
    event: K,
    ...args: Parameters<ChatClientToServerEvents[K]>
  ): void {
    if (!this.socket?.connected) {
      console.error('Chat socket not connected');
      return;
    }
    this.socket.emit(event, ...args);
  }

  on<K extends keyof ChatServerToClientEvents>(
    event: K,
    callback: ChatServerToClientEvents[K]
  ): () => void {
    if (!this.socket) {
      console.error('Chat socket not initialized');
      return () => {};
    }

    console.log(`[ChatSocket] Adding listener for event: ${event as string}`);
    console.log(`[ChatSocket] Current listeners count for ${event as string}: ${this.listeners.get(event as string)?.size || 0}`);

    // Сохраняем callback в нашей Map
    if (!this.listeners.has(event as string)) {
      this.listeners.set(event as string, new Set());
    }
    this.listeners.get(event as string)!.add(callback as Function);

    // Подписываемся на событие
    this.socket.on(event, callback as any);
    
    console.log(`[ChatSocket] After adding - listeners count for ${event as string}: ${this.listeners.get(event as string)?.size || 0}`);
    
    // Return unsubscribe function
    return () => {
      console.log(`[ChatSocket] Unsubscribing from event: ${event as string}`);
      console.log(`[ChatSocket] Before removing - listeners count for ${event as string}: ${this.listeners.get(event as string)?.size || 0}`);
      
      if (this.socket && this.listeners.has(event as string)) {
        this.socket.off(event, callback as any);
        this.listeners.get(event as string)!.delete(callback as Function);
        
        console.log(`[ChatSocket] After removing - listeners count for ${event as string}: ${this.listeners.get(event as string)?.size || 0}`);
        
        // Если больше нет слушателей для этого события, удаляем запись
        if (this.listeners.get(event as string)!.size === 0) {
          this.listeners.delete(event as string);
          console.log(`[ChatSocket] No more listeners for ${event as string}, removed from map`);
        }
      } else {
        console.log(`[ChatSocket] Cannot unsubscribe - socket or listeners not found`);
      }
    };
  }

  get isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

export const chatSocket = new ChatSocket();
